import { useState } from 'react'
import { ChevronRight, ChevronDown, Copy, Users } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Skeleton } from '@/components/ui/skeleton'
import { Button } from '@/components/ui/button'
import { useDepartmentTree } from '@/hooks/useDepartments'
import type { ApiDepartment } from '@/types/department'
import { DepartmentPermissionCopyDialog } from './DepartmentPermissionCopyDialog'

interface DepartmentTreeSelectProps {
	selectedId: number | null
	onSelect: (id: number) => void
	onPermissionsCopied?: (targetIds: number[]) => void
}

// 递归树节点组件
function TreeNode({
	node,
	selectedId,
	onSelect,
	level = 0,
}: {
	node: ApiDepartment
	selectedId: number | null
	onSelect: (id: number) => void
	level?: number
}) {
	const [isExpanded, setIsExpanded] = useState(level < 2)
	const hasChildren = node.children && node.children.length > 0
	const isSelected = selectedId === node.id

	const handleToggle = (e: React.MouseEvent) => {
		e.stopPropagation()
		if (hasChildren) {
			setIsExpanded(!isExpanded)
		}
	}

	const handleSelect = () => {
		onSelect(node.id)
	}

	return (
		<div className="select-none">
			<div
				className={cn(
					'flex items-center gap-1 px-2 py-1.5 cursor-pointer rounded-md transition-colors',
					isSelected ? 'bg-primary/10 text-primary' : 'hover:bg-muted'
				)}
				style={{ paddingLeft: `${level * 16 + 8}px` }}
				onClick={handleSelect}
			>
				{/* 展开/折叠按钮 */}
				<button
					onClick={handleToggle}
					className={cn(
						'h-4 w-4 flex items-center justify-center shrink-0',
						!hasChildren && 'invisible'
					)}
				>
					{hasChildren && (
						isExpanded ? (
							<ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
						) : (
							<ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
						)
					)}
				</button>

				{/* 部门名称 */}
				<span className="flex-1 truncate text-sm font-medium">
					{node.name}
				</span>

				{/* 用户数量 */}
				<span className="flex items-center gap-0.5 text-xs text-muted-foreground">
					<Users className="h-3 w-3" />
					{node.user_count || 0}
				</span>
			</div>

			{/* 子节点 */}
			{hasChildren && isExpanded && (
				<div>
					{node.children!.map((child) => (
						<TreeNode
							key={child.id}
							node={child}
							selectedId={selectedId}
							onSelect={onSelect}
							level={level + 1}
						/>
					))}
				</div>
			)}
		</div>
	)
}

export function DepartmentTreeSelect({ selectedId, onSelect, onPermissionsCopied }: DepartmentTreeSelectProps) {
	const { data: treeData, isLoading, isError, error, refetch } = useDepartmentTree()
	const [copyDialogOpen, setCopyDialogOpen] = useState(false)

	return (
		<div className="flex flex-col h-full">
			{/* 头部 */}
			<div className="space-y-3 border-b p-4">
				<div>
					<h2 className="font-semibold text-lg">选择部门</h2>
					<p className="text-sm text-muted-foreground mt-1">
						选择部门以配置其权限
					</p>
				</div>
				<Button
					type="button"
					variant="outline"
					size="sm"
					className="w-full"
					disabled={!selectedId || isLoading || isError || !treeData || treeData.length === 0}
					onClick={() => setCopyDialogOpen(true)}
				>
					<Copy className="mr-2 h-4 w-4" />
					复制当前部门权限
				</Button>
			</div>

			{/* 树内容 */}
			<div className="flex-1 overflow-auto py-2">
				{isLoading ? (
					<div className="px-4 space-y-2">
						{Array.from({ length: 4 }).map((_, i) => (
							<Skeleton key={i} className="h-8 w-full" />
						))}
					</div>
				) : isError ? (
					<div className="px-4 py-8 text-center">
						<p className="text-sm text-destructive mb-2">加载失败</p>
						<p className="text-xs text-muted-foreground mb-4">{(error as Error)?.message}</p>
						<Button variant="outline" size="sm" onClick={() => refetch()}>
							重试
						</Button>
					</div>
				) : !treeData || treeData.length === 0 ? (
					<div className="px-4 py-8 text-center">
						<p className="text-sm text-muted-foreground">暂无部门数据</p>
					</div>
				) : (
					treeData.map((node) => (
						<TreeNode
							key={node.id}
							node={node}
							selectedId={selectedId}
							onSelect={onSelect}
						/>
					))
				)}
			</div>

			<DepartmentPermissionCopyDialog
				open={copyDialogOpen}
				onOpenChange={setCopyDialogOpen}
				sourceDepartmentId={selectedId}
				departments={treeData || []}
				onCopied={onPermissionsCopied}
			/>
		</div>
	)
}
